import {
  GoabBadge,
  GoabDrawer,
  GoabDropdown,
  GoabDropdownItem,
  GoabDropdownOnChangeDetail,
  GoabPagination,
  GoabPaginationOnChangeDetail,
  GoabTab,
  GoabTable,
  GoabTableOnSortDetail,
  GoabTableSortHeader,
  GoabTabs,
  GoabTabsOnChangeDetail,
  GoabText,
} from '@abgov/angular-components';
import '@abgov/web-components';
import { DateTransform, ResourcePipe, SnackbarService } from '@ala/common-ui';
import { CommonModule, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  Output,
  signal,
  viewChild,
} from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import {
  CapacityReportOrderByInput,
  CapacityReportStatus,
  Sort,
} from '@libs/services/graphql/generated/ala-backend.service';
import { Mutable } from '@libs/services/graphql/utilities/to-mutable';
import { Subject, takeUntil } from 'rxjs';
import { DataEntryStateService } from '../../../../services/data-entry-state.service';
import { StoreStateService } from '../../../../services/store.service';
import { constants } from '../../../../shared/constants/constants';
import { BadgeTransformPipe } from '../../../../shared/pipes/badge-transform.pipe';
import { EnumReverseMappingPipe } from '../../../../shared/pipes/enum-reverse-mapping.pipe';
import { IDataEntry } from '../../../data-entry/viewmodels/data-entry.viewmodel';
import { NoReportsFoundComponent } from '../../../error/components/no-reports-found/no-reports-found.component';
import { CapacityReportVM } from '../../viewmodels/facility.viewmodel';

@Component({
  selector: 'app-view-entries',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    CommonModule,
    GoabText,
    GoabDropdown,
    GoabDropdownItem,
    GoabDrawer,
    GoabTable,
    GoabTableSortHeader,
    GoabPagination,
    ResourcePipe,
    DateTransform,
    EnumReverseMappingPipe,
    BadgeTransformPipe,
    GoabTabs,
    GoabTab,
    GoabBadge,
    NgTemplateOutlet,
    RouterOutlet,
    NoReportsFoundComponent,
  ],
  templateUrl: './view-entries.component.html',
  styleUrl: './view-entries.component.scss',
})
export class ViewEntriesComponent implements AfterViewInit, OnInit, OnDestroy {
  pageId = 'VIEWENTRIES';
  router = inject(Router);
  route = inject(ActivatedRoute);
  dataEntryStateService = inject(DataEntryStateService);
  storeService = inject(StoreStateService);
  snackbarService = inject(SnackbarService);

  userFacilities = input<CapacityReportVM[]>([]);
  facilitiesCount = input<number>(0);
  drawerClosed = output<void>();

  @Output() facilitySelected = new EventEmitter<string>();
  @Output() searchClicked = new EventEmitter<void>();

  page = input<number>(1);
  tab = input<CapacityReportStatus[]>([
    CapacityReportStatus.NOTSTARTED,
    CapacityReportStatus.INPROGRESS,
  ]);
  perPageCount = input<number>(constants.VIEW_ENTRIES_PERPAGE_COUNT[0]);
  sort = input<Mutable<CapacityReportOrderByInput>>({});
  perPageCountOptions = signal<number[]>([]);
  drawerRef = viewChild.required('drawer', { read: ElementRef });

  pageChanged = output<{
    tab: CapacityReportStatus[];
    page: number;
    perPageCount: number;
    sort: Mutable<CapacityReportOrderByInput>;
  }>();

  tabChanged = output<{
    tab: CapacityReportStatus[];
    page: number;
    perPageCount: number;
    sort: Mutable<CapacityReportOrderByInput>;
  }>();
  perPageCountChanged = output<{
    tab: CapacityReportStatus[];
    page: number;
    perPageCount: number;
    sort: Mutable<CapacityReportOrderByInput>;
  }>();
  sortOrderChanged = output<{
    tab: CapacityReportStatus[];
    page: number;
    perPageCount: number;
    sort: Mutable<CapacityReportOrderByInput>;
  }>();
  resetPerPageCount = output<number>();

  isDrawerOpen = signal<boolean>(false);
  selectedFacilityName = '';
  selectedFacilityStatus = '';
  dataEntry = signal<IDataEntry | undefined>(undefined);

  private destroy$ = new Subject<void>();

  constructor() {
    // effect(() => {
    //   console.log(
    //     '[View entries] page, tab, perPageCount',
    //     this.page(),
    //     this.tab(),
    //     this.perPageCount()
    //   );
    // });
    effect(() => {
      this.dataEntry.set(this.storeService.dataEntry());
    });
    effect(() => {
      const entry = this.dataEntryStateService.currentCapacityReport();
      if (entry) {
        this.selectedFacilityStatus = entry.status;
      }
    });
    effect(() => {
      const pageCountOptions = [];
      if (this.tab()) {
        for (const perPage of constants.VIEW_ENTRIES_PERPAGE_COUNT) {
          if (perPage < this.facilitiesCount()) {
            pageCountOptions.push(perPage);
          }
        }
        // Only add the total count as option,  if all valid options are not present
        if (
          pageCountOptions.length < constants.VIEW_ENTRIES_PERPAGE_COUNT.length
        ) {
          pageCountOptions.push(this.facilitiesCount());
        }
        this.perPageCountOptions.set(pageCountOptions);
        this.resetPerPageCount.emit(pageCountOptions[0]);
      }
    });
    effect(() => {
      if (this.isDrawerOpen()) {
        const goaDrawerShadowRoot =
          this.drawerRef()?.nativeElement?.querySelector(
            'goa-drawer'
          )?.shadowRoot;
        if (!goaDrawerShadowRoot) return;
        const focusTrap = goaDrawerShadowRoot.querySelector('goa-focus-trap');
        const section = focusTrap.querySelector('section.drawer-actions');
        section.style.display = 'none';
        const header = focusTrap.querySelector('.header');
        header.style.alignItems = 'start';
        const drawerCloseX: HTMLElement =
          goaDrawerShadowRoot.querySelector('goa-icon-button');
        if (drawerCloseX) {
          (drawerCloseX as HTMLElement).style.paddingTop = '.6875rem';
        }
      }
    });
    effect(() => {
      const dataEntrySubmitted = this.dataEntryStateService.submissionResult();
      if (dataEntrySubmitted) {
        if (dataEntrySubmitted.success) {
          this.snackbarService.showSuccess(
            `${
              (dataEntrySubmitted.context as { facilityName: string })
                ?.facilityName || 'Unknown facility'
            } report has been successfully submitted, and your smoking policy confirmation has been recorded`
          );
        } else {
          this.snackbarService.showError(
            `${
              (dataEntrySubmitted.context as { facilityName: string })
                ?.facilityName || 'Unknown facility'
            } report was not submitted: ${
              dataEntrySubmitted.error || 'Unknown error'
            }`
          );
        }
        this.dataEntryStateService.submissionResult.set(undefined);
      }
    });
  }
  ngOnInit() {
    this.dataEntryStateService.editorClosed$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.isDrawerOpen.set(false);
      });
  }

  ngAfterViewInit(): void {
    // we need to remove the facility name underline; however:
    // what looks like the underline is actually something elusive
    // and needs hard hunting down:
    this.setupDrawerWatcher();
    setTimeout(() => {
      this.applyHeaderUnderline();
    }, 0);
  }

  private setupDrawerWatcher(): void {
    const drawerElement = document.querySelector('goa-drawer');
    if (!drawerElement) return;

    let hasRunOnce = false; // Prevent multiple runs

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'open' &&
          !hasRunOnce
        ) {
          hasRunOnce = true;
          setTimeout(() => {
            this.removeFacilityHeadingUnderline();
          }, 50);
        }
      });
    });

    observer.observe(drawerElement, {
      attributes: true,
      attributeFilter: ['open'],
    });
  }

  private removeFacilityHeadingUnderline(): void {
    const shadowRoot = document.querySelector('goa-drawer')?.shadowRoot;
    if (!shadowRoot) return;

    const style = document.createElement('style');
    style.id = 'persistent-border-remover';
    style.textContent = `
    /* Target by class name */
    .header,
    .drawer-content,
    .drawer-actions,
    [class*="header"],
    [class*="drawer-content"],
    [class*="drawer-actions"] {
      border: none !important;
      border-bottom: none !important;
      box-shadow: none !important;
      background-image: none !important;
    }

    /* Target pseudo-elements more broadly */
    .header::before,
    .header::after,
    [class*="header"]::before,
    [class*="header"]::after {
      display: none !important;
      content: none !important;
      border: none !important;
      background: none !important;
      box-shadow: none !important;
    }
  `;
    shadowRoot.appendChild(style);

    // Also try targeting by data attributes or other stable selectors
    const headerElement = shadowRoot.querySelector(
      '.header, [data-testid*="header"], [class*="header"]'
    ) as HTMLElement;
    if (headerElement) {
      headerElement.style.setProperty('box-shadow', 'none', 'important');
      headerElement.style.setProperty('background', 'transparent', 'important');
      headerElement.style.setProperty('background-image', 'none', 'important');
    }
  }

  private applyHeaderUnderline(): void {
    const goaDrawerShadowRoot = document.querySelector(
      'goab-drawer > goa-drawer'
    )?.shadowRoot;
    if (!goaDrawerShadowRoot) return;

    const headerElement = goaDrawerShadowRoot.querySelector(
      'goa-focus-trap > div > div > div[class*="header"]'
    ) as HTMLElement;
    if (!headerElement) return;

    headerElement.style.setProperty(
      'border-bottom',
      '1px solid var(--goa-color-greyscale-300, #707070)',
      'important'
    );
  }

  onClickSearch() {
    this.searchClicked.emit();
  }

  tableOnSort($event: GoabTableOnSortDetail) {
    this.sortOrderChanged.emit({
      tab: this.tab(),
      //Reset page count to #1
      page: this.page(),
      sort: { [$event.sortBy]: $event.sortDir === 1 ? 'asc' : 'desc' },
      perPageCount: this.perPageCount(),
    });
  }
  handlePageChange($event: GoabPaginationOnChangeDetail) {
    this.pageChanged.emit({
      tab: this.tab(),
      page: $event.page,
      perPageCount: this.perPageCount(),
      sort: this.sort(),
    });
  }
  handlePerPageCountChangeEvent($event: GoabDropdownOnChangeDetail) {
    this.perPageCountChanged.emit({
      tab: this.tab(),
      //Reset page count to #1
      page: 1,
      perPageCount: Number($event.value),
      sort: this.sort(),
    });
  }

  onTabChange($event: GoabTabsOnChangeDetail) {
    switch ($event.tab) {
      case 1:
        this.tabChanged.emit({
          tab: [
            CapacityReportStatus.NOTSTARTED,
            CapacityReportStatus.INPROGRESS,
          ],
          // Reset page # and perPageCount on Tab change
          page: 1,
          perPageCount: constants.VIEW_ENTRIES_PERPAGE_COUNT[0],
          sort: !Object.keys(this.sort()).length
            ? { capacityReportPeriodDueDate: Sort.ASC }
            : { ...this.sort(), capacityReportPeriodDueDate: Sort.ASC },
        });
        break;
      case 2:
        this.tabChanged.emit({
          tab: [CapacityReportStatus.COMPLETED],
          // Reset page # and perPageCount on Tab change
          page: 1,
          perPageCount: constants.VIEW_ENTRIES_PERPAGE_COUNT[0],
          sort: !Object.keys(this.sort()).length
            ? { capacityReportPeriodDueDate: Sort.DESC }
            : { ...this.sort(), capacityReportPeriodDueDate: Sort.DESC },
        });
        break;
      default:
        this.tabChanged.emit({
          tab: [
            CapacityReportStatus.NOTSTARTED,
            CapacityReportStatus.INPROGRESS,
          ],
          // Reset page # and perPageCount on Tab change
          page: 1,
          perPageCount: constants.VIEW_ENTRIES_PERPAGE_COUNT[0],
          sort: !Object.keys(this.sort()).length
            ? { capacityReportPeriodDueDate: Sort.ASC }
            : { ...this.sort(), capacityReportPeriodDueDate: Sort.ASC },
        });
    }
  }

  onFacilityClick(facilityId: string, facilityName: string, status: string) {
    // console.log('Facility clicked:', facilityId, facilityName);
    this.selectedFacilityName = facilityName;
    this.selectedFacilityStatus = status;
    this.router.navigate([facilityId, 'edit'], { relativeTo: this.route });
    this.isDrawerOpen.set(true);
  }

  onDrawerClose() {
    this.dataEntryStateService.notifyEditorClosed();
    this.router.navigate(['view-entries']);
    this.isDrawerOpen.set(false);
    this.drawerClosed.emit();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
